import { applyZavvyDisabled } from '../Components/FocusMode/Zavvy/orgFeatures';

/**
 * Shared SSE (Server-Sent Events) stream reader utility.
 *
 * Handles the fetch → ReadableStream → line-buffer → event-dispatch pipeline
 * that all AI streaming endpoints in this app share. Both token and done
 * events follow the same wire format:
 *
 *   data: <JSON-encoded-string>          (one token per event)
 *   event: done\ndata: <JSON-object>     (terminal event with metadata)
 *
 * RFC 8895 §3.4 note: we strip at most ONE leading U+0020 space after the
 * colon — we do NOT use .trim() because that would swallow leading spaces
 * in LLM tokens (e.g. the `" policy"` token would become `"policy"`).
 *
 * @param {string} url          Full endpoint URL.
 * @param {object} body         Request body (will be JSON-serialised).
 * @param {object} [headers]    Extra headers merged on top of Content-Type + Auth.
 * @param {(token: string) => void} onToken   Called for each streamed text token.
 * @param {(result: object) => void} onDone   Called once with the final metadata object.
 * @param {(error: Error) => void} onError    Called on HTTP or stream errors.
 * @returns {AbortController}   Abort to cancel mid-stream.
 */

const REASONING_PART_TYPES = new Set(['reasoning', 'thought', 'thinking', 'reasoning_text']);
const TEXT_PART_TYPES = new Set(['text', 'output_text', 'output_text_delta', 'input_text', 'refusal']);
const PART_KEYS = new Set(['type', 'text', 'index', 'annotations', 'output_text', 'content', 'refusal']);

const looksLikeContentPart = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const kind = String(value.type || '').toLowerCase();
  if (REASONING_PART_TYPES.has(kind) || TEXT_PART_TYPES.has(kind)) return true;
  const keys = Object.keys(value);
  if (!keys.length || keys.some((key) => !PART_KEYS.has(key))) return false;
  return ['text', 'output_text', 'content', 'refusal'].some((key) => value[key] != null);
};

const looksLikeContentParts = (value) => {
  if (looksLikeContentPart(value)) return true;
  if (Array.isArray(value) && value.length) {
    return value.every((item) => typeof item === 'string' || looksLikeContentParts(item));
  }
  return false;
};

/**
 * Detect whatever the model sent and return display text.
 * Plain strings pass through. Content-part objects / arrays are flattened.
 * JSON strings of parts are parsed first. Other JSON (json_object answers) is left alone.
 */
export const sseTokenToText = (token) => {
  if (token == null) return '';
  if (typeof token === 'string') {
    const trimmed = token.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(token);
        if (looksLikeContentParts(parsed)) return sseTokenToText(parsed);
      } catch {
        return token;
      }
    }
    return token;
  }
  if (Array.isArray(token)) return token.map(sseTokenToText).join('');
  if (typeof token === 'object') {
    if (!looksLikeContentPart(token)) {
      try {
        return JSON.stringify(token);
      } catch {
        return '';
      }
    }
    const kind = String(token.type || '').toLowerCase();
    if (REASONING_PART_TYPES.has(kind)) return '';
    if (typeof token.text === 'string') return token.text;
    if (typeof token.output_text === 'string') return token.output_text;
    if (typeof token.content === 'string') return token.content;
    if (token.content != null) return sseTokenToText(token.content);
    if (typeof token.refusal === 'string') return token.refusal;
    return '';
  }
  return String(token);
};

/** Shown when a chat message is over the product cap, including a raw Pydantic 422. */
export const MESSAGE_TOO_LONG = 'This message is longer than 120,000 characters. Attach the document with + instead of pasting the whole text.';

const humaniseValidation = (msg) => {
  if (typeof msg !== 'string' || !msg) return '';
  const text = msg.replace(/^Value error,\s*/, '');
  if (/^String should have at most \d+ characters$/.test(text)) return MESSAGE_TOO_LONG;
  return text;
};

/**
 * Turns a FastAPI-style error `detail` into text. `detail` is a string for most errors but an
 * object (`{code, message}`, e.g. 409 turn_in_progress, 429 limit_reached) or a list of objects
 * (422 validation), and `new Error(object)` renders as "[object Object]".
 */
export const sseErrorDetailToText = (detail, fallback) => {
  if (typeof detail === 'string' && detail) return humaniseValidation(detail);
  if (Array.isArray(detail)) {
    const msg = detail.map((d) => (typeof d === 'string' ? humaniseValidation(d) : humaniseValidation(d?.msg || d?.message))).filter(Boolean).join('; ');
    if (msg) return msg;
  } else if (detail && typeof detail === 'object') {
    const msg = detail.message || detail.msg || detail.code;
    if (typeof msg === 'string' && msg) return humaniseValidation(msg);
  }
  return fallback;
};

/**
 * options.requireTerminal: when true, a stream that closes without a `done` or `error` event is
 * reported through onError ("Connection closed") instead of ending silently, so callers do not
 * stay in a streaming state forever after a dropped connection.
 */
export const createSSEStream = (url, body, headers = {}, onToken, onDone, onError, onEvent, options = {}) => {
  const controller = new AbortController();
  const jwtToken = localStorage.getItem('jwtToken');

  const run = async () => {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(jwtToken && { Authorization: `Bearer ${jwtToken}` }),
          ...headers,
        },
        body: JSON.stringify(body),
      });
    } catch (err) {
      if (err.name === 'AbortError') return;
      onError?.(err);
      return;
    }

    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      let code;
      try {
        const errBody = await response.json();
        detail = sseErrorDetailToText(errBody?.detail, detail);
        if (errBody?.detail && typeof errBody.detail === 'object' && !Array.isArray(errBody.detail) && errBody.detail.code) {
          code = errBody.detail.code;
        }
      } catch { /* ignore */ }
      const err = new Error(detail);
      err.status = response.status;
      if (code) err.code = code;
      if (code === 'zavvy_disabled') applyZavvyDisabled();
      onError?.(err);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let lastEventType = 'message';
    let sawTerminal = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            // Strip exactly one optional leading space per RFC 8895 §3.4
            lastEventType = line.startsWith('event: ') ? line.slice(7) : line.slice(6);
          } else if (line.startsWith('data:')) {
            // Strip exactly one optional leading space — preserves token-internal spaces
            const raw = line.startsWith('data: ') ? line.slice(6) : line.slice(5);

            if (typeof onEvent === 'function') {
              let parsed = raw;
              try {
                parsed = JSON.parse(raw);
              } catch {
                parsed = raw;
              }
              onEvent(lastEventType, parsed);
            }

            if (lastEventType === 'done') {
              sawTerminal = true;
              try {
                onDone?.(JSON.parse(raw));
              } catch {
                onDone?.({ sources: [] });
              }
            } else if (lastEventType === 'error') {
              // Backend emits `event: error\ndata: {"error": "...", "reason": "..."}` or
              // `{"code": "...", "message": "..."}` (Zavvy)
              // on a failed chat completion (e.g. an unsupported deployment/param
              // combo, or a reasoning model exhausting its token budget on
              // internal reasoning). Route to onError — never onToken, or the
              // raw JSON gets painted into the chat as if it were the answer.
              let message = raw || 'Stream error';
              let reason;
              try {
                const parsed = JSON.parse(raw);
                // Zavvy emits {code, message}; the chat completion path emits {error, reason}.
                message = parsed?.message || parsed?.error || message;
                reason = parsed?.reason;
              } catch {
                // raw wasn't JSON — fall back to it as the message verbatim.
              }
              const err = new Error(message);
              if (reason) err.reason = reason;
              sawTerminal = true;
              onError?.(err);
              return; // backend closes the stream right after `event: error`
            } else {
              // Tokens are JSON-encoded strings by the backend (json.dumps).
              // Parsing restores leading/trailing spaces that .trim() would destroy.
              // gpt-5.6-sol and similar models may emit content parts as objects
              // ({type, text}); concatenating those in React becomes "[object Object]".
              try {
                onToken?.(sseTokenToText(JSON.parse(raw)));
              } catch {
                onToken?.(raw); // plain-text fallback for non-JSON backends
              }
            }
            lastEventType = 'message';
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      onError?.(err);
      return;
    }

    // The reader finished with no terminal event: the server or a proxy closed the connection early.
    if (options.requireTerminal && !sawTerminal && !controller.signal.aborted) {
      const err = new Error('Connection closed before the answer finished. Please try again.');
      err.code = 'connection_closed';
      onError?.(err);
    }
  };

  run();
  return controller;
};
